// Shipping utilities for calculating shipping costs
import type { ShippingCategory, ShippingRate } from '../admin/types';

export interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  shipping_category?: ShippingCategory;
  image_url?: string;
}

/**
 * Determine the highest shipping category from a list of cart items
 * Ranking: small < medium < large
 */
export function getHighestShippingCategory(cartItems: CartItem[]): ShippingCategory {
  if (cartItems.some(item => item.shipping_category === 'large')) {
    return 'large';
  }
  if (cartItems.some(item => item.shipping_category === 'medium')) {
    return 'medium';
  }
  return 'small';
}

/**
 * Fetch shipping rates from Supabase
 */
export async function getShippingRates(): Promise<ShippingRate[]> {
  try {
    const { supabase } = await import('../admin/lib/supabaseClient');
    if (!supabase?.from) throw new Error('No DB client');
    const { data, error } = await supabase
      .from('shipping_rates')
      .select('*')
      .order('price', { ascending: true });
    if (error) throw error;
    return data || [];
  } catch (e) {
    console.warn('Falling back to static shipping rates', e);
    return [
      { category: 'small', price: 12000 },
      { category: 'medium', price: 20000 },
      { category: 'large', price: 35000 }
    ] as ShippingRate[];
  }
}

/**
 * Get shipping cost for a specific category
 */
export async function getShippingCost(category: ShippingCategory): Promise<number> {
  try {
    const { supabase } = await import('../admin/lib/supabaseClient');
    if (!supabase?.from) throw new Error('No DB client');
    const { data, error } = await supabase
      .from('shipping_rates')
      .select('price')
      .eq('category', category)
      .single();
    if (error) throw error;
    return data?.price || 0;
  } catch (e) {
    console.warn('Using fallback shipping cost', e);
    const fallbackRates = { small: 12000, medium: 20000, large: 35000 };
    return fallbackRates[category];
  }
}

/**
 * Calculate total shipping cost for cart items
 */
export async function calculateShippingCost(cartItems: CartItem[]): Promise<{
  category: ShippingCategory;
  cost: number;
  description: string;
}> {
  if (cartItems.length === 0) {
    return { category: 'small', cost: 0, description: 'No items' };
  }

  const highestCategory = getHighestShippingCategory(cartItems);
  const cost = await getShippingCost(highestCategory);
  
  const descriptions = {
    small: 'Small package delivery',
    medium: 'Medium package delivery', 
    large: 'Large package delivery'
  };

  return {
    category: highestCategory,
    cost,
    description: descriptions[highestCategory]
  };
}

/**
 * Format shipping cost for display (convert from cents to rand)
 */
export function formatShippingCost(costInCents: number): string {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR'
  }).format(costInCents / 100);
}

/**
 * Get shipping category display information
 */
export function getShippingCategoryInfo(category: ShippingCategory) {
  const info = {
    small: {
      name: 'Small Package',
      description: 'Documents, accessories, small parts',
      icon: '📦'
    },
    medium: {
      name: 'Medium Package', 
      description: 'Standard car parts, tools',
      icon: '📋'
    },
    large: {
      name: 'Large Package',
      description: 'Engines, body panels, heavy items',
      icon: '🚛'
    }
  };
  
  return info[category];
}
